import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation } from "convex/react";
import { Avatar, Box, Button, ButtonGroup, Card, HStack, Heading, Stack, Text } from "@chakra-ui/react";
import { useQuery } from "@/lib/convex";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { DetailSkeleton } from "../components/LoadingSkeletons";
import { SinglePersonPicker } from "../components/SinglePersonPicker";
import { toastError, toastSuccess } from "../components/ui/toaster";
import { useEffectivePersonId } from "../impersonation";

function formatDay(day: string) {
  return new Intl.DateTimeFormat("en-GB", { dateStyle: "medium" }).format(new Date(`${day}T12:00:00Z`));
}

/**
 * A student's co-supervisor, on their programme page.
 *
 * Everybody who can open the page reads it -- the student included. The
 * supervisor and the activity admins can also record one, change it, or end
 * it. A student has at most one at a time, so recording a new one ends the
 * current one; the earlier ones stay listed underneath.
 */
export function CoSupervisorCard({ studentPersonId }: { studentPersonId: Id<"persons"> }) {
  const effectivePersonId = useEffectivePersonId() as Id<"persons"> | null;
  const record = useQuery(api.coSupervision.forStudent, { effectivePersonId, studentPersonId });
  const setCoSupervisor = useMutation(api.coSupervision.setCoSupervisor);
  const endCoSupervision = useMutation(api.coSupervision.endCoSupervision);

  const [mode, setMode] = useState<"view" | "choose" | "confirmEnd">("view");
  const [chosen, setChosen] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // The directory only once somebody is choosing: it is the whole institute.
  const directory = useQuery(api.persons.listCeMMies, mode === "choose" ? {} : "skip");

  if (record === undefined) {
    return <DetailSkeleton label="Loading the co-supervisor" />;
  }

  const current = record.current?.coSupervisor ?? null;

  async function save() {
    if (chosen === null) {
      return;
    }
    setSaving(true);
    try {
      await setCoSupervisor({ effectivePersonId, studentPersonId, coSupervisorPersonId: chosen as Id<"persons"> });
      toastSuccess("Co-supervisor recorded.");
      setMode("view");
      setChosen(null);
    } catch (error) {
      toastError(error instanceof Error ? error.message : "Could not record the co-supervisor.");
    } finally {
      setSaving(false);
    }
  }

  async function end() {
    setSaving(true);
    try {
      await endCoSupervision({ effectivePersonId, studentPersonId });
      toastSuccess("Co-supervision ended.");
      setMode("view");
    } catch (error) {
      toastError(error instanceof Error ? error.message : "Could not end the co-supervision.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card.Root borderWidth="1px" borderColor="border">
      <Card.Body gap={4} p={5}>
        <HStack justify="space-between" align="center" gap={3} flexWrap="wrap">
          <Heading size="md">Co-supervisor</Heading>
          {record.viewerMayEdit && mode === "view" ? (
            <ButtonGroup size="sm" variant="outline" gap={2}>
              <Button onClick={() => { setChosen(null); setMode("choose"); }}>
                {current ? "Change" : "Record"}
              </Button>
              {current ? <Button onClick={() => setMode("confirmEnd")}>End</Button> : null}
            </ButtonGroup>
          ) : null}
        </HStack>

        {current ? (
          <HStack gap={3}>
            <Avatar.Root size="md">
              <Avatar.Fallback name={current.fullName} />
              {current.profileImageUrl ? <Avatar.Image src={current.profileImageUrl} alt={`${current.fullName} profile`} /> : null}
            </Avatar.Root>
            <Stack gap={0}>
              {current.shortName ? (
                <Link to="/cemmies/$shortName" params={{ shortName: current.shortName }}>
                  <Text fontWeight="semibold" _hover={{ textDecoration: "underline" }}>{current.fullName}</Text>
                </Link>
              ) : (
                <Text fontWeight="semibold">{current.fullName}</Text>
              )}
              <Text fontSize="sm" color="fg.muted">Since {formatDay(record.current!.startDay)}</Text>
            </Stack>
          </HStack>
        ) : (
          <Text fontSize="sm" color="fg.muted">No co-supervisor recorded.</Text>
        )}

        {mode === "choose" ? (
          <Stack gap={3}>
            <SinglePersonPicker
              label={current ? "New co-supervisor" : "Co-supervisor"}
              people={directory?.persons ?? []}
              selectedPersonId={chosen}
              onChange={setChosen}
            />
            {current ? (
              <Text fontSize="xs" color="fg.muted">
                {current.fullName} stops being co-supervisor today. Steps for the co-supervisor that are
                still open move to the new one.
              </Text>
            ) : null}
            <ButtonGroup size="sm" gap={2}>
              <Button colorPalette="blue" disabled={chosen === null} loading={saving} onClick={() => void save()}>Save</Button>
              <Button variant="ghost" onClick={() => setMode("view")}>Cancel</Button>
            </ButtonGroup>
          </Stack>
        ) : null}

        {mode === "confirmEnd" && current ? (
          <Stack gap={3}>
            <Text fontSize="sm">
              End {current.fullName}&rsquo;s co-supervision today, without a successor?
            </Text>
            <ButtonGroup size="sm" gap={2}>
              <Button colorPalette="orange" loading={saving} onClick={() => void end()}>End co-supervision</Button>
              <Button variant="ghost" onClick={() => setMode("view")}>Cancel</Button>
            </ButtonGroup>
          </Stack>
        ) : null}

        {record.past.length > 0 ? (
          <Box>
            <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.16em" color="fg.muted">
              Before
            </Text>
            <Stack gap={0.5} mt={1}>
              {record.past.map((row) => (
                <Text key={row._id} fontSize="sm" color="fg.muted">
                  {row.coSupervisor?.fullName ?? "Somebody since removed"}, {formatDay(row.startDay)} – {formatDay(row.endDay!)}
                </Text>
              ))}
            </Stack>
          </Box>
        ) : null}
      </Card.Body>
    </Card.Root>
  );
}
